import { useContext } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAlert } from "capitalrx-components-2.0/lib/general";

import { ANALYTIC_EVENTS, logEvent } from "@/App/service";

import { AuthenticationContext } from "../context";
import { MemberEligibilityContext } from "../context/MemberEligibilityContext";
import {
  CancelRequest,
  CancelResponse,
  ConfirmPaymentIntentRequest,
  ConfirmPaymentIntentResponse,
  CreateMedicarePrescriptionPaymentPlanRefundRequest,
  CreateMedicarePrescriptionPaymentPlanRefundResponse,
  InitiatePaymentIntentRequest,
  InitiatePaymentIntentResponse,
  InitiateSetupIntentRequest,
  InitiateSetupIntentResponse,
  ListMedicarePrescriptionPaymentPlanInvoicesRequest,
  ListMedicarePrescriptionPaymentPlanInvoicesResponse,
  ListMedicarePrescriptionPaymentPlanPaymentRequest,
  ListMedicarePrescriptionPaymentPlanPaymentResponse,
  ListUpcomingPaymentsRequest,
  ListUpcomingPaymentsResponse,
  MedicarePrescriptionPaymentPlanEstimateRequest,
  MedicarePrescriptionPaymentPlanEstimateResponse,
  MedicarePrescriptionPaymentPlanInvoice,
  MemberPaymentConfiguration,
  ParticipateRequest,
  ParticipateResponse,
  SetMedicarePrescriptionPaymentPlanCommunicationPreferencesRequest,
  SetMedicarePrescriptionPaymentPlanCommunicationPreferencesResponse,
  UpdatePaymentConfigurationRequest,
  UpdatePaymentConfigurationResponse,
} from "../types/medicarePrescriptionPaymentPlan";
import { ViewRequest, getEligibilityBasedQueryKey, getViewRequest, request } from "./request";

export const participate = async (authToken: string, payload: ParticipateRequest) => {
  const response = (await request<{}, ParticipateResponse>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/participate",
      body: payload,
    },
    authToken,
  )) as ParticipateResponse;
  return response;
};

export const useMedicarePrescriptionPaymentPlanParticipateMutation = () => {
  const queryClient = useQueryClient();
  const { updateMemberEligibilityWithFlags } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  return useMutation({
    mutationFn: async (participateData: ParticipateRequest) => participate(await getAuthToken(), participateData),
    onSuccess: (participateResponse: ParticipateResponse) => {
      queryClient.setQueryData(["medicarePrescriptionPaymentPlanParticipate"], participateResponse);
      updateMemberEligibilityWithFlags(participateResponse.member_flags);
    },
  });
};

export const cancel = async (authToken: string, payload: CancelRequest) => {
  const response = (await request<CancelRequest, CancelResponse>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/cancel",
      body: payload,
    },
    authToken,
  )) as CancelResponse;
  return response;
};

export const useCancelMedicarePrescriptionPaymentPlanMutation = () => {
  const queryClient = useQueryClient();
  const { updateMemberEligibilityWithFlags } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  return useMutation({
    mutationFn: async (cancelData: CancelRequest) => cancel(await getAuthToken(), cancelData),
    onSuccess: (cancelData: CancelResponse) => {
      queryClient.setQueryData(["medicarePrescriptionPaymentPlanParticipate"], cancelData);
      updateMemberEligibilityWithFlags(cancelData.member_flags);
    },
  });
};

export const initiateSetupIntent = async (authToken: string, payload: InitiateSetupIntentRequest) => {
  return request<InitiateSetupIntentRequest, InitiateSetupIntentResponse>(
    {
      endpoint: "/member/payment/initiate_setup",
      body: payload,
    },
    authToken,
  );
};

export const initiatePaymentIntent = async (authToken: string, payload: InitiatePaymentIntentRequest) => {
  return request<InitiatePaymentIntentRequest, InitiatePaymentIntentResponse>(
    {
      endpoint: "/member/payment/initiate_payment",
      body: payload,
    },
    authToken,
  );
};

export const confirmPaymentIntent = async (authToken: string, payload: ConfirmPaymentIntentRequest) => {
  return request<ConfirmPaymentIntentRequest, ConfirmPaymentIntentResponse>(
    {
      endpoint: "/member/payment/confirm_payment",
      body: payload,
    },
    authToken,
  );
};

export const updatePaymentConfiguration = async (authToken: string, payload: UpdatePaymentConfigurationRequest) => {
  return request<UpdatePaymentConfigurationRequest, UpdatePaymentConfigurationResponse>(
    {
      endpoint: "/member/payment/configuration/update",
      body: payload,
    },
    authToken,
  );
};

export const useUpdateMemberPaymentConfigurationMutation = () => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryClient = useQueryClient();
  const showAlert = useAlert();

  return useMutation({
    mutationFn: async (data: UpdatePaymentConfigurationRequest) => {
      return await updatePaymentConfiguration(await getAuthToken(), data);
    },
    onSuccess: () => {
      logEvent(ANALYTIC_EVENTS.MPPP.PAYMENT_INFORMATION.changePaymentInformation);
      queryClient.invalidateQueries({
        queryKey: ["memberPaymentConfiguration"],
        refetchType: "all",
      });
      showAlert({
        message: "Successfully changed payment method",
        type: "success",
        style: "toast",
      });
    },
    onError: (err: string | Error) => {
      showAlert({
        message: typeof err === "string" ? err : err.message,
        type: "error",
        style: "banner",
      });
    },
  });
};

export const getPaymentConfiguration = async (authToken: string, body: Partial<ViewRequest> & { plan_year: number }) => {
  return request<Partial<ViewRequest> & { plan_year: number }, MemberPaymentConfiguration>(
    {
      endpoint: "/member/payment/configuration/get",
      body: { ...body },
    },
    authToken,
  );
};

export const useGetMemberPaymentConfigurationQuery = (planYear: number) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  return useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getPaymentConfiguration(await getAuthToken(), {
          ...getViewRequest(memberEligibility),
          plan_year: planYear,
        });
      } else {
        return getPaymentConfiguration(await getAuthToken(), { plan_year: planYear });
      }
    },
    queryKey: ["memberPaymentConfiguration", planYear],
    retry: 1,
  });
};

export const estimatePayments = async (authToken: string, body: MedicarePrescriptionPaymentPlanEstimateRequest & Partial<ViewRequest>) => {
  return await request<MedicarePrescriptionPaymentPlanEstimateRequest, MedicarePrescriptionPaymentPlanEstimateResponse>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/estimate_payment",
      body: { ...body },
    },
    authToken,
  );
};

export const useEstimateMedicarePrescriptionPaymentPlanPayments = (filters: MedicarePrescriptionPaymentPlanEstimateRequest) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = [
    "estimateMedicarePrescriptionPaymentPlanPayment",
    filters.year,
    filters.start_month,
    filters.true_out_of_pocket_per_month,
    filters.total_true_out_of_pocket_amount,
    ...getEligibilityBasedQueryKey(memberEligibility),
  ];

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return estimatePayments(await getAuthToken(), { ...getViewRequest(memberEligibility), ...filters });
      } else {
        return estimatePayments(await getAuthToken(), { ...filters });
      }
    },
    queryKey: queryKey,
    enabled: !!filters.start_month && !!filters.year,
  });

  return query;
};

const listMedicarePrescriptionPaymentPlanUpcomingPayments = async (
  authToken: string,
  body: ListUpcomingPaymentsRequest | (ListUpcomingPaymentsRequest & ViewRequest),
) => {
  return request<typeof body, ListUpcomingPaymentsResponse>(
    {
      endpoint: "/medicare_prescription_payment_plan/upcoming_payments/list",
      body: { ...body },
    },
    authToken,
  );
};

export const useListMedicarePrescriptionPaymentPlanUpcomingPaymentsQuery = (filters: ListUpcomingPaymentsRequest) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = [
    "listMedicarePrescriptionPaymentPlanUpcomingPayments",
    filters.page_number,
    ...getEligibilityBasedQueryKey(memberEligibility),
  ];

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMedicarePrescriptionPaymentPlanUpcomingPayments(await getAuthToken(), {
          ...filters,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return listMedicarePrescriptionPaymentPlanUpcomingPayments(await getAuthToken(), filters);
      }
    },
    queryKey: queryKey,
  });

  return query;
};

const getTotalMedicarePrescriptionPaymentPlanTransactions = async (authToken: string, body: Partial<ViewRequest> & { plan_year: number }) => {
  return request<Partial<ViewRequest> & { plan_year: number }, { total_balance: string; latest_transaction_date: string }>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/get_transaction_balance",
      body: { ...body },
    },
    authToken,
  );
};

export const useGetTotalMedicarePrescriptionPaymentPlanTransactions = (planYear: number) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["getMedicarePrescriptionPaymentPlanPaymentTransactionTotal", planYear, ...getEligibilityBasedQueryKey(memberEligibility)];

  return useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getTotalMedicarePrescriptionPaymentPlanTransactions(await getAuthToken(), {
          ...getViewRequest(memberEligibility),
          plan_year: planYear,
        });
      } else {
        return getTotalMedicarePrescriptionPaymentPlanTransactions(await getAuthToken(), { plan_year: planYear });
      }
    },
    queryKey: queryKey,
  });
};

const listMedicarePrescriptionPaymentPlanPayment = async (
  authToken: string,
  body: ListMedicarePrescriptionPaymentPlanPaymentRequest & Partial<ViewRequest>,
) => {
  return request<typeof body, ListMedicarePrescriptionPaymentPlanPaymentResponse>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/list_transactions",
      body: { ...body },
    },
    authToken,
  );
};

export const useListMedicarePrescriptionPaymentPlanPaymentQuery = (filters: ListMedicarePrescriptionPaymentPlanPaymentRequest) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["listMedicarePrescriptionPaymentPlanPaymentHistory", filters.plan_year, ...getEligibilityBasedQueryKey(memberEligibility)];
  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMedicarePrescriptionPaymentPlanPayment(await getAuthToken(), {
          ...filters,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return listMedicarePrescriptionPaymentPlanPayment(await getAuthToken(), {
          ...filters,
        });
      }
    },
    queryKey: queryKey,
  });

  return query;
};

const listMedicarePrescriptionPaymentPlanInvoices = async (
  authToken: string,
  body: Partial<ViewRequest> & ListMedicarePrescriptionPaymentPlanInvoicesRequest,
) => {
  return request<typeof body, ListMedicarePrescriptionPaymentPlanInvoicesResponse>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/list_invoices",
      body: { ...body },
    },
    authToken,
  );
};

export const useListMedicarePrescriptionPaymentPlanInvoicesQuery = (filters: ListMedicarePrescriptionPaymentPlanInvoicesRequest) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["listMedicarePrescriptionPaymentPlanInvoices", filters.plan_year, filters.end_date, ...getEligibilityBasedQueryKey(memberEligibility)];

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMedicarePrescriptionPaymentPlanInvoices(await getAuthToken(), {
          ...filters,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return listMedicarePrescriptionPaymentPlanInvoices(await getAuthToken(), {
          ...filters,
        });
      }
    },
    queryKey: queryKey,
  });

  return query;
};

const setMedicarePrescriptionPaymentPlanCommunicationPreferences = async (
  authToken: string,
  body: SetMedicarePrescriptionPaymentPlanCommunicationPreferencesRequest & Partial<ViewRequest>,
) => {
  return request<typeof body, SetMedicarePrescriptionPaymentPlanCommunicationPreferencesResponse>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/set_communication_preferences",
      body: { ...body },
    },
    authToken,
  );
};

export const useSetMedicarePrescriptionPaymentPlanCommunicationPreferencesMutation = () => {
  const { memberEligibility, isViewingDataholder, updateMemberEligibilityWithFlags } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);

  return useMutation({
    mutationFn: async (data: SetMedicarePrescriptionPaymentPlanCommunicationPreferencesRequest) => {
      if (isViewingDataholder) {
        return setMedicarePrescriptionPaymentPlanCommunicationPreferences(await getAuthToken(), {
          ...data,
          ...getViewRequest(memberEligibility),
        });
      } else {
        return setMedicarePrescriptionPaymentPlanCommunicationPreferences(await getAuthToken(), { ...data });
      }
    },
    onSuccess: (responseData: SetMedicarePrescriptionPaymentPlanCommunicationPreferencesResponse) => {
      updateMemberEligibilityWithFlags(responseData.member_flags);
    },
  });
};

const listMedicarePrescriptionPaymentPlanDueInvoices = async (authToken: string, body: Partial<ViewRequest> & { plan_year: number }) => {
  return request<typeof body, { readonly results: MedicarePrescriptionPaymentPlanInvoice[]; readonly result_count: string }>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/invoice/due",
      body: { ...body },
    },
    authToken,
  );
};

export const useListMedicarePrescriptionPaymentPlanDueInvoicesQuery = (planYear: number) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["listMedicarePrescriptionPaymentPlanDueInvoices", planYear, ...getEligibilityBasedQueryKey(memberEligibility)];

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMedicarePrescriptionPaymentPlanDueInvoices(await getAuthToken(), {
          ...getViewRequest(memberEligibility),
          plan_year: planYear,
        });
      } else {
        return listMedicarePrescriptionPaymentPlanDueInvoices(await getAuthToken(), { plan_year: planYear });
      }
    },
    queryKey: queryKey,
  });

  return query;
};

const listMedicarePrescriptionPaymentPlanUpcomingInvoices = async (authToken: string, body: Partial<ViewRequest> & { plan_year: number }) => {
  return request<typeof body, { readonly results: MedicarePrescriptionPaymentPlanInvoice[]; readonly result_count: string }>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/invoice/upcoming",
      body: { ...body },
    },
    authToken,
  );
};

export const useListMedicarePrescriptionPaymentPlanUpcomingInvoicesQuery = (planYear: number) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["listMedicarePrescriptionPaymentPlanUpcomingInvoices", planYear, ...getEligibilityBasedQueryKey(memberEligibility)];

  const query = useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return listMedicarePrescriptionPaymentPlanUpcomingInvoices(await getAuthToken(), {
          ...getViewRequest(memberEligibility),
          plan_year: planYear,
        });
      } else {
        return listMedicarePrescriptionPaymentPlanUpcomingInvoices(await getAuthToken(), { plan_year: planYear });
      }
    },
    queryKey: queryKey,
  });

  return query;
};

const getMedicarePrescriptionPaymentPlanRefundStatus = async (authToken: string, body: Partial<ViewRequest> & { plan_year: number }) => {
  return request<Partial<ViewRequest> & { plan_year: number }, { refundable_amount: string; message: string }>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/transaction/refund/status",
      body: { ...body },
    },
    authToken,
  );
};

export const useGetMedicarePrescriptionPaymentPlanRefundStatus = (planYear: number) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryKey = ["getMedicarePrescriptionPaymentPlanRefundStatus", planYear, ...getEligibilityBasedQueryKey(memberEligibility)];

  return useQuery({
    queryFn: async () => {
      if (isViewingDataholder) {
        return getMedicarePrescriptionPaymentPlanRefundStatus(await getAuthToken(), {
          ...getViewRequest(memberEligibility),
          plan_year: planYear,
        });
      } else {
        return getMedicarePrescriptionPaymentPlanRefundStatus(await getAuthToken(), { plan_year: planYear });
      }
    },
    queryKey: queryKey,
  });
};

const createMedicarePrescriptionPaymentPlanRefund = async (
  authToken: string,
  body: CreateMedicarePrescriptionPaymentPlanRefundRequest & Partial<ViewRequest>,
) => {
  return request<typeof body, CreateMedicarePrescriptionPaymentPlanRefundResponse>(
    {
      endpoint: "/member/medicare_prescription_payment_plan/transaction/refund/create",
      body: { ...body },
    },
    authToken,
  );
};

export const useCreateMedicarePrescriptionPaymentPlanRefundStatusMutation = (planYear: number) => {
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { getAuthToken } = useContext(AuthenticationContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMedicarePrescriptionPaymentPlanRefundRequest) => {
      if (isViewingDataholder) {
        return createMedicarePrescriptionPaymentPlanRefund(await getAuthToken(), {
          ...data,
          ...getViewRequest(memberEligibility),
          plan_year: planYear,
        });
      } else {
        return createMedicarePrescriptionPaymentPlanRefund(await getAuthToken(), {
          ...data,
          plan_year: planYear,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getMedicarePrescriptionPaymentPlanRefundStatus"] });
    },
  });
};
